import { Loader2 } from "lucide-react";
import {
  mappingDialogBodyClass,
  mappingDialogContentClass,
  mappingDialogFooterClass,
  mappingDialogHeaderClass,
  mappingDialogLoadingOverlayClass
} from "../../../../screens/mapping_tool/mappingModalStyles";
import { Button } from "../../../ui/common/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../ui/common/dialog";
import { Form } from "../../../ui/common/form";
import { FormInput } from "../../../ui/Input/FormInput/FormInput";
import { FormTextarea } from "../../../ui/Input/FormTextArea/FormTextarea";
import { EDIT_MAPPING_COPY } from "../constants/mappingVersion.constants";
import { useEditMappingForm } from "../hooks/useEditMappingForm";
import { EditMappingResult, EditMode } from "../types/mappingVersion.types";
import { validateVersionName } from "../utils/versionName.utils";
import EditModeRadioGroup from "./EditModeRadioGroup";

interface EditMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  drawingToolId: string | undefined;
  ownerId: string;
  sourceId: string;
  mappingType: number;
  onSuccess: (result: EditMappingResult) => void;
  /** Whether "Create a new version" may be picked -- see `canCreateNewVersion` in versioningHelpers. */
  canCreateNewVersion?: boolean;
  /**
   * Supplier User restriction: hides the update/new-version choice entirely so the mapping can
   * only ever be updated in place. Callers must also pass `canCreateNewVersion={false}` alongside
   * this so the underlying form can't submit a new-version mode even if triggered programmatically.
   */
  restrictToUpdateOnly?: boolean;
}

const EditMappingDialog = ({
  open,
  onOpenChange,
  drawingToolId,
  ownerId,
  sourceId,
  mappingType,
  onSuccess,
  canCreateNewVersion,
  restrictToUpdateOnly = false
}: EditMappingDialogProps) => {
  const {
    form,
    mode,
    nextVersionNumber,
    existingVersionNames,
    isLoadingNextVersionInfo,
    isSubmitting,
    canCreateNewVersion: allowNewVersion,
    onSubmit
  } = useEditMappingForm({
    open,
    drawingToolId,
    ownerId,
    sourceId,
    mappingType,
    canCreateNewVersion,
    onSuccess: result => {
      onOpenChange(false);
      onSuccess(result);
    }
  });

  const isNewVersion = mode === EditMode.NewVersion;
  const submitLabel = isNewVersion
    ? `${EDIT_MAPPING_COPY.submitNewVersionPrefix} New Version`
    : EDIT_MAPPING_COPY.submitUpdate;

  return (
    <Dialog
      open={open}
      // A submit in flight must not be dismissable -- see DeleteMappingDialog for the same guard.
      onOpenChange={next => {
        if (!isSubmitting) {
          onOpenChange(next);
        }
      }}
    >
      <DialogContent className={mappingDialogContentClass}>
        <DialogHeader className={mappingDialogHeaderClass}>
          <DialogTitle>{EDIT_MAPPING_COPY.title}</DialogTitle>
          <DialogDescription className="mt-1">{EDIT_MAPPING_COPY.description}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={onSubmit}>
            <div className={mappingDialogBodyClass}>
              {isSubmitting ? (
                <div className={mappingDialogLoadingOverlayClass}>
                  <Loader2 className="tw-h-6 tw-w-6 tw-animate-spin tw-text-primary" />
                </div>
              ) : null}

              <FormTextarea
                control={form.control}
                name="reason"
                label={EDIT_MAPPING_COPY.reasonLabel}
                placeholder={EDIT_MAPPING_COPY.reasonPlaceholder}
                required
                rows={3}
                autoFocus
                // The shared Textarea has no focus styling of its own -- pin its focus ring to
                // the app's primary green instead of the browser default (see
                // DeleteConfirmPhraseInput for the same fix on the shared Input).
                className="focus-visible:tw-border-ring focus-visible:tw-ring-2 focus-visible:tw-ring-ring/50"
              />

              <EditModeRadioGroup
                control={form.control}
                name="mode"
                disableNewVersion={!allowNewVersion}
                restrictToUpdateOnly={restrictToUpdateOnly}
              />

              {isNewVersion ? (
                <>
                  {/* <VersionNumberDisplay versionNumber={nextVersionNumber} isLoading={isLoadingNextVersionInfo} /> */}
                  <FormInput
                    control={form.control}
                    name="versionName"
                    label={EDIT_MAPPING_COPY.versionNameLabel}
                    required
                    rules={{
                      validate: value => validateVersionName(value, existingVersionNames)
                    }}
                    className="focus-visible:tw-border-ring focus-visible:tw-ring-2 focus-visible:tw-ring-ring/50"
                  />
                </>
              ) : null}
            </div>

            <DialogFooter className={`mt-1 ${mappingDialogFooterClass}`}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isSubmitting}
                onClick={() => onOpenChange(false)}
              >
                {EDIT_MAPPING_COPY.cancel}
              </Button>
              <Button type="submit" size="sm" disabled={isSubmitting}>
                {submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditMappingDialog;
